import { Box, Button, CircularProgress, Typography } from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import { Email, Refresh } from "@mui/icons-material";

/** The first load, before any RFQ is on screen. */
export function RfqListLoading() {
  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        height: "50vh",
        flexDirection: "column",
        gap: 2,
      }}
    >
      <CircularProgress />
      <Typography>Loading RFQs...</Typography>
    </Box>
  );
}

/**
 * The first load failed and there is nothing to show; with cached RFQs the
 * error banner above the list reports the failure instead.
 */
export function RfqListConnectionError({ onRetry }: { onRetry: () => void }) {
  const theme = useTheme();
  return (
    <Box
      sx={{
        p: 4,
        textAlign: "center",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 2,
        maxWidth: "600px",
        mx: "auto",
        mt: 4,
        borderRadius: 1,
        bgcolor: alpha(theme.palette.error.main, 0.05),
        border: `1px solid ${alpha(theme.palette.error.main, 0.2)}`,
      }}
    >
      <Typography variant="h6" color="error">
        Connection Error
      </Typography>
      <Typography color="text.secondary" sx={{ mb: 2 }}>
        Unable to fetch RFQs. Please check your connection and try again.
      </Typography>
      <Button variant="contained" onClick={onRetry} sx={{ px: 3 }} startIcon={<Refresh />}>
        Retry
      </Button>
    </Box>
  );
}

/** No data at all (the query has neither data nor an error). */
export function RfqListNoData({ onRefresh, onCheckEmails, isRefreshing }: { onRefresh: () => void; onCheckEmails: () => void; isRefreshing: boolean }) {
  return (
    <Box
      sx={{
        p: 4,
        textAlign: "center",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 2,
        maxWidth: "600px",
        mx: "auto",
        mt: 4,
      }}
    >
      <Typography variant="h6" color="warning.main">
        No Data Available
      </Typography>
      <Typography color="text.secondary" sx={{ mb: 2 }}>
        No RFQs were found. Try refreshing or checking for new emails.
      </Typography>
      <Box sx={{ display: "flex", gap: 2 }}>
        <Button variant="contained" onClick={onRefresh} sx={{ px: 3 }} startIcon={<Refresh />}>
          Refresh
        </Button>
        <Button
          variant="outlined"
          onClick={onCheckEmails}
          sx={{ px: 3 }}
          startIcon={<Email />}
          disabled={isRefreshing}
        >
          Check Emails
        </Button>
      </Box>
    </Box>
  );
}
